/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { List, Map } from 'immutable';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
export const BookingStage = Object.freeze({
  Initialization:                       'Initialization',
  NewAppointmentValidation:             'NewAppointmentValidation',
  NewAppointmentForbiddenError:         'NewAppointmentForbiddenError',
  NewAppointmentSetup:                  'NewAppointmentSetup',
  NewAppointmentSetupError:             'NewAppointmentSetupError',
  NewAppointmentSelection:              'NewAppointmentSelection',
  NewAppointmentCreation:               'NewAppointmentCreation',
  NewAppointmentCreationError:          'NewAppointmentCreationError',
  NewAppointmentForbiddenCreationError: 'NewAppointmentForbiddenCreationError',
  ExistingAppointmentSetup:             'ExistingAppointmentSetup',
  ExistingAppointmentDisplay:           'ExistingAppointmentDisplay',
  ExistingAppointmentDeletion:          'ExistingAppointmentDeletion',
  ForcedAppointmentSubmission:          'ForcedAppointmentSubmission',
});

export const BookingStageAction = Object.freeze({
  SelectBookingMode:                 'SelectBookingMode',
  ValidateDatasourceAvailability:    'ValidateDatasourceAvailability',
  SetSlotDuration:                   'SetSlotDuration',
  SetTimeZone:                       'SetTimeZone',
  FetchCalendarData:                 'FetchCalendarData',
  BuildCalendarAvailability:         'BuildCalendarAvailability',
  ValidateCalendarAvailability:      'ValidateCalendarAvailability',
  CreateAppointment:                 'CreateAppointment',
  SetAppointment:                    'SetAppointment',
  DisplayAppointment:                'DisplayAppointment',
  DisplayBookingForm:                'DisplayBookingForm',
  NotifyBookedSlot:                  'NotifyBookedSlot',
  NotifyLimitedCalendarAvailability: 'NotifyLimitedCalendarAvailability',
  ClearAppointment:                  'ClearAppointment',
  ClearDaySelection:                 'ClearDaySelection',
  SubmitAnswer:                      'SubmitAnswer',
  ClearSubmittedAnswer:              'ClearSubmittedAnswer',
});

export const BookingStageActionStatus = Object.freeze({
  Pending:    'Pending',
  InProgress: 'InProgress',
  Completed:  'Completed',
});

export const BookingStageStateAction = Object.freeze({
  SetStage:            'SetStage',
  ClaimStageAction:    'ClaimStageAction',
  CompleteStageAction: 'CompleteStageAction',
  SetStageActions:     'SetStageActions',
  AppendStageActions:  'AppendStageActions',
});

export function buildPendingStageAction(type) {
  return new Map({ type, status: BookingStageActionStatus.Pending });
}

export const defaultStageActionsMap = new Map({
  [BookingStage.Initialization]: new List([
    buildPendingStageAction(BookingStageAction.SelectBookingMode),
  ]),
  [BookingStage.NewAppointmentValidation]: new List([
    buildPendingStageAction(BookingStageAction.ValidateDatasourceAvailability),
  ]),
  [BookingStage.NewAppointmentForbiddenError]: new List([
    buildPendingStageAction(BookingStageAction.SubmitAnswer),
  ]),
  [BookingStage.NewAppointmentSetup]: new List([
    buildPendingStageAction(BookingStageAction.SetSlotDuration),
    buildPendingStageAction(BookingStageAction.SetTimeZone),
    buildPendingStageAction(BookingStageAction.FetchCalendarData),
    buildPendingStageAction(BookingStageAction.BuildCalendarAvailability),
    buildPendingStageAction(BookingStageAction.ValidateCalendarAvailability),
  ]),
  [BookingStage.NewAppointmentCreation]: new List([
    buildPendingStageAction(BookingStageAction.CreateAppointment),
    buildPendingStageAction(BookingStageAction.SetAppointment),
    buildPendingStageAction(BookingStageAction.SubmitAnswer),
    buildPendingStageAction(BookingStageAction.DisplayAppointment),
  ]),
  [BookingStage.NewAppointmentForbiddenCreationError]: new List([
    buildPendingStageAction(BookingStageAction.ValidateDatasourceAvailability),
    buildPendingStageAction(BookingStageAction.NotifyBookedSlot),
  ]),
  [BookingStage.ExistingAppointmentSetup]: new List([
    buildPendingStageAction(BookingStageAction.SetTimeZone),
    buildPendingStageAction(BookingStageAction.SetAppointment),
    buildPendingStageAction(BookingStageAction.DisplayAppointment),
  ]),
  [BookingStage.ExistingAppointmentDeletion]: new List([
    buildPendingStageAction(BookingStageAction.ClearDaySelection),
    buildPendingStageAction(BookingStageAction.ClearAppointment),
    buildPendingStageAction(BookingStageAction.ClearSubmittedAnswer),
    buildPendingStageAction(BookingStageAction.DisplayBookingForm),
  ]),
  [BookingStage.ForcedAppointmentSubmission]: new List([
    buildPendingStageAction(BookingStageAction.SubmitAnswer),
  ]),
});

export const defaultBookingStageState = new Map({
  currentStage:        BookingStage.Initialization,
  pendingStageActions: defaultStageActionsMap.get(BookingStage.Initialization),
});

export function bookingStageStateReducer(state, action) {
  switch (action.type) {
    case BookingStageStateAction.SetStage: {
      if (state.get('currentStage') !== action.target) {
        return state
          .set('currentStage', action.target)
          .set('pendingStageActions', defaultStageActionsMap.get(action.target) || new List());
      }

      return state;
    }
    case BookingStageStateAction.ClaimStageAction: {
      const claimedStageActionIndex = state.get('pendingStageActions')
        .findIndex((stageAction) => stageAction.get('type') === action.targetActionType);

      if (claimedStageActionIndex >= 0) {
        const claimedStageAction = state.getIn(['pendingStageActions', claimedStageActionIndex])
          .set('status', BookingStageActionStatus.InProgress);

        return state.setIn(['pendingStageActions', claimedStageActionIndex], claimedStageAction);
      }

      return state;
    }
    case BookingStageStateAction.CompleteStageAction: {
      const completedStageActionIndex = state.get('pendingStageActions')
        .findIndex((stageAction) => stageAction.get('type') === action.targetActionType);

      if (completedStageActionIndex >= 0) {
        return state.update(
          'pendingStageActions',
          (pendingStageActions) => pendingStageActions.delete(completedStageActionIndex),
        );
      }

      return state;
    }
    case BookingStageStateAction.SetStageActions: {
      return state.set('pendingStageActions', action.newActions);
    }
    case BookingStageStateAction.AppendStageActions: {
      return state.update('pendingStageActions', (pendingStageActions) => pendingStageActions.concat(action.newActions));
    }
    default: {
      throw new Error(`Invalid action type: ${action.type}`);
    }
  }
}
